import _ from 'lodash';
import { ActionTypes } from '../actions/projectActions';

import getSteps, { digitalSteps } from '../util/customizationNavigation';
import { PRIMARY_CUSTOMIZATION_TYPES } from '../constants/Cards';
import FeatureFlags from '../../util/featureFlags';

const fontVariationsMapper = (fonts, fontFamily) => (
  _.reduce(fonts, (result, font) => {
    if (font.font_family === fontFamily) {
      result.push({
        id: font.id,
        displayName: font.display_name.replace(fontFamily, '').trim(),
        fontWeight: font.font_weight,
        fontStyle: font.font_style,
        fontFamily: font.font_family
      });
    }
    // Here we split the list of variation on regular and non regular
    const splittedFonts = _.partition(result, { fontWeight: 400 });
    // Brings "Regular" to the top as default and then the rest
    const topFonts = _.sortBy(splittedFonts[0], topFont => (topFont.displayName === 'Regular' ? 0 : 1));
    const regularFonts = _.sortBy(splittedFonts[1], ['fontWeight']);
    // Return a merge of both lists keeping custom order
    return _.union(topFonts, regularFonts);
  }, [])
);

const fontsMapper = (fonts) => {
  /**
   * Here we split the font list to separate recommended from not recommended
   * then we filter uniques from the not recommended list
   * then we merge them keeping order so we have recommended on top
   * once we have a proper list we reduce them so as to fill a new array with needed values
   * TODO: see if we can chain lodash methods https://lodash.com/docs/4.17.11#chain
   */
  const splittedResult = _.partition(fonts, { template_referenced: true });
  const uniqueFonts = _.uniqBy(splittedResult[1], 'font_family');
  const fontList = _.union(splittedResult[0], uniqueFonts);
  const { mappedFonts } = _.reduce(fontList, (result, font) => {
    // Only add fonts not in result array
    if (!_.find(result.mappedFonts, { name: font.font_family })) {
      result.mappedFonts.push({
        class: font.font_family.replace(/\s/g, '-').toLowerCase(),
        id: font.id,
        isGlobal: font.globally_available,
        name: font.font_family,
        recommended: font.template_referenced,
        variations: fontVariationsMapper(fonts, font.font_family),
      });
    }
    return result;
  }, { mappedFonts: [] });

  return mappedFonts;
};

const breadCrumbMapper = (entities, cardSteps) => {
  const { customizations } = entities;
  const { orderedItems } = cardSteps;
  const hasRSVP = _.some(customizations, { type: 'RSVP', inactive: false });

  const breadCrumbSteps = _.reduce(orderedItems, (result, step) => {
    // Push only used menus
    if (step !== 'recipients' && step !== 'addressing') {
      result.push(step);
    }

    // Handle RSVP on changes to options
    if (hasRSVP && step === 'rsvpEnvelope' && !result.includes('rsvpEnvelope') && orderedItems.includes('rsvpEnvelope')) {
      result.push('rsvpEnvelope');
    } else if (!hasRSVP && step === 'rsvpEnvelope' && result.includes('rsvpEnvelope') && orderedItems.includes('rsvpEnvelope')) {
      result.splice(result.indexOf('rsvpEnvelope'), 1);
    }
    return result;
  }, []);


  return breadCrumbSteps;
};

const initialState = {
  uuid: null,
  type: null,
  splitOrderId: null,
  size: null,
  customizations: [],
  entities: {},
  ui: {
    activeField: null,
    steps: {},
    breadCrumbs: []
  },
  fonts: {
    mapped: [],
    raw: []
  },
  updatingCustomization: null,
  customizationTypeToUUID: {},
  isFetching: false,
  isSaving: false,
  busy: false,
  isAddingToCart: false,
  digitalStatus: {
    status: null,
    uuid: null
  },
  digitalOrderRequested: false,
  digitalProofRequested: false,
};

function projectReducer(state = initialState, action = {}) {
  switch (action.type) {
    case ActionTypes.REQUEST_PROJECT: {
      return { ...state, isFetching: true };
    }
    case ActionTypes.RECEIVE_PROJECT: {
      const { result: project, entities } = action.payload.project;
      const leadCustomizationUUID = _.find(project.customizations, (customizationUUID) => {
        const customization = entities.customizations[customizationUUID];
        return _.includes(PRIMARY_CUSTOMIZATION_TYPES, customization.type);
      });
      const leadCustomization = entities.customizations[leadCustomizationUUID] || null;
      const { type } = leadCustomization;
      const size = leadCustomization
        && leadCustomization.variation
        && leadCustomization.variation.option_values
        && leadCustomization.variation.option_values.size;

      const medium = FeatureFlags.get('magnetInvites') ? leadCustomization.medium : 'PAPER';
      const steps = medium === 'DIGITAL' ? digitalSteps : getSteps(type, size);

      return {
        ...state,
        accountId: project.account_id,
        completedAt: project.completed_at,
        id: project.id,
        name: project.name,
        uuid: project.uuid,
        medium,
        type,
        size,
        customizations: project.customizations,
        customizationTypeToUUID: project.customizationTypeToUUID,
        entities,
        ui: {
          ...state.ui,
          activeField: null,
          steps,
          breadCrumbs: breadCrumbMapper(entities, steps),
        }
      };
    }
    case ActionTypes.REQUEST_UPDATE_CUSTOMIZATION: {
      const { customizationUUID } = action.payload;
      return {
        ...state,
        updatingCustomization: customizationUUID,
        isSaving: true
      };
    }
    // case ActionTypes.REQUEST_CUSTOMIZATION: {
    //   return { ...state };
    // }
    case ActionTypes.RECEIVE_CUSTOMIZATION: {
      const { result: customization, entities } = action.payload.customization;

      let customizationType = customization.type; // eslint-disable-line
      // IMPORTANT: this is a hack use during sku migration to get around multiple customizations with
      // type: ENVELOPE - need to come up with something better
      if (customization.type === 'ENVELOPE') {
        if (/rsvp/i.test(customization.name)) {
          customizationType = 'RSVP_ENVELOPE';
        } else {
          customizationType = 'INVITATION_ENVELOPE';
        }
      }
      return {
        ...state,
        updatingCustomization: null,
        entities: {
          ...state.entities,
          elements: {
            ...state.entities.elements,
            ...entities.elements,
          },
          pages: {
            ...state.entities.pages,
            ...entities.pages,
          },
          customizations: {
            ...state.entities.customizations,
            [customization.uuid]: customization
          }
        },
        customizationTypeToUUID: {
          ...state.customizationTypeToUUID,
          [customizationType]: customization.uuid
        },
        isSaving: false
      };
    }
    case ActionTypes.RECEIVE_ADD_TEXT_ELEMENT: {
      // set activeField to new element (if content_type is text) / insert in 'entities.elements'
      // then append element.uuid to array of associated page in entities.pages
      const {
        element,
        pageUUID
      } = action.payload;
      const page = state.entities.pages[pageUUID];
      return {
        ...state,
        ui: {
          ...state.ui,
          // only update activeField if content_type is text, prevents adding elements with linked element
          // from losing focus after cloned linked element is created (i.e. rsvp/meal option bullet point images)
          activeField: element.content_type === 'TEXT' ? element.uuid : state.ui.activeField,
        },
        entities: {
          ...state.entities,
          elements: {
            ...state.entities.elements,
            [element.uuid]: element
          },
          pages: {
            ...state.entities.pages,
            [pageUUID]: {
              ...page,
              elements: [...page.elements, element.uuid]
            }
          }
        },
      };
    }

    case ActionTypes.RECEIVE_RESTORED_ELEMENT: {
      const { elements, lastChanges, pageUUID } = action.payload;
      const newElements = {};
      const newElementUUIDs = [];
      _.forEach(elements, (elm, index) => {
        newElements[elm.uuid] = Object.assign({}, elm, lastChanges[index].value);
        newElementUUIDs.push(elm.uuid);
      });
      return {
        ...state,
        ui: {
          ...state.ui,
          activeField: (elements.length > 1) ? elements[1].uuid : elements[0].uuid,
        },
        entities: {
          ...state.entities,
          elements: {
            ...state.entities.elements,
            ...newElements
          },
          pages: {
            ...state.entities.pages,
            [pageUUID]: {
              ...state.entities.pages[pageUUID],
              elements: [...state.entities.pages[pageUUID].elements, ...newElementUUIDs]
            }
          }
        },
      };
    }

    // TODO: this could be named in a more generic way - RECEIVE_ELEMENT or something similar
    case ActionTypes.RECEIVE_DELETE_ELEMENT: {
      const { pageUUID, elementUUIDs } = action.payload;
      let nextElements = state.entities.elements;
      elementUUIDs.forEach((elementUUID) => {
        nextElements = _.omit(state.entities.elements, elementUUID);
      });
      return {
        ...state,
        ui: {
          ...state.ui,
          activeField: null // remove focus
        },
        entities: {
          ...state.entities,
          elements: nextElements,
          pages: {
            ...state.entities.pages,
            [pageUUID]: {
              ...state.entities.pages[pageUUID],
              elements: state.entities.pages[pageUUID].elements.filter(uuid => !_.includes(elementUUIDs, uuid))
            }
          }
        },
      };
    }
    case ActionTypes.RECEIVE_DELETE_ELEMENT_IMAGE: {
      const { elementUUID, element } = action.payload;
      return {
        ...state,
        entities: {
          ...state.entities,
          elements: {
            ...state.entities.elements,
            [elementUUID]: element
          }
        }
      };
    }
    case ActionTypes.SET_ACTIVE_FIELD: {
      const { activeField } = action.payload;
      return {
        ...state,
        ui: {
          ...state.ui,
          activeField
        }
      };
    }
    // Customization fonts actions
    case ActionTypes.GET_CUSTOMIZATION_FONTS_REQUEST: {
      return { ...state, isFetching: true };
    }
    case ActionTypes.GET_CUSTOMIZATION_FONTS_SUCCESS: {
      const { fonts } = action.payload;
      const mappedFonts = fontsMapper(fonts);
      return {
        ...state,
        isFetching: false,
        fonts: {
          mapped: mappedFonts,
          raw: fonts
        }
      };
    }
    case ActionTypes.GET_CUSTOMIZATION_FONTS_FAILURE: {
      return { ...state, isFetching: false, fonts: [] };
    }
    case ActionTypes.REQUEST_ADD_PROJECT_TO_CART: {
      return { ...state, isAddingToCart: true };
    }
    case ActionTypes.RECEIVE_ADD_PROJECT_TO_CART: {
      return { ...state, isAddingToCart: false };
    }
    case ActionTypes.RECEIVE_COMPLETE_DIGITAL_PROJECT: {
      const { async_request_uuid: asyncRequestUUID, async_request_status: asyncRequestStatus } = action.payload;
      return {
        ...state,
        digitalStatus: {
          uuid: asyncRequestUUID,
          status: asyncRequestStatus
        },
        digitalOrderRequested: false
      };
    }
    case ActionTypes.RECEIVE_DIGITAL_PROJECT_SUCCESS: {
      return {
        ...state,
        digitalStatus: {
          uuid: null,
          status: null
        },
        digitalOrderRequested: false
      };
    }
    case ActionTypes.REQUEST_DIGITAL_ORDER: {
      return {
        ...state,
        digitalOrderRequested: true
      };
    }
    case ActionTypes.CREATE_DIGITAL_PROOF_REQUEST: {
      return {
        ...state,
        digitalProofRequested: true
      };
    }
    case ActionTypes.UPDATE_DIGITAL_PROOF_REQUEST: {
      return {
        ...state,
        digitalProofRequested: false
      };
    }
    default: {
      return state;
    }
  }
}

export default projectReducer;



// WEBPACK FOOTER //
// ./src/cards/reducers/projectReducer.js